import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { FiInfo } from 'react-icons/fi';
import api from '../services/api';
import DraggableMediaList from '../components/DraggableMediaList';
import './PropertyForm.css';

const CreateProperty = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [uploadingVideos, setUploadingVideos] = useState(false);

  // Owner/Host management
  const [isOwner, setIsOwner] = useState(true);
  const [isHost, setIsHost] = useState(true);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [hostSearch, setHostSearch] = useState('');
  const [ownerSearchResults, setOwnerSearchResults] = useState([]);
  const [hostSearchResults, setHostSearchResults] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'apartment',
    property_category: 'single_unit',
    listing_subtype: 'entire_place',
    square_footage: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    bedrooms: 1,
    bathrooms: 1,
    beds: 1,
    max_guests: 2,
    base_guests: 2,
    price_per_night: '',
    extra_guest_price: 0,
    cleaning_fee: 0,
    service_fee: 0,
    check_in_time: '15:00',
    check_out_time: '11:00',
    minimum_nights: 1,
    maximum_nights: 30,
    cancellation_policy: 'flexible',
    house_rules: '',
    amenity_ids: []
  });

  useEffect(() => {
    fetchAmenities();
  }, []);

  const fetchAmenities = async () => {
    try {
      const response = await api.get('/api/properties/amenities');
      setAmenities(response.data.amenities);
    } catch (error) {
      console.error('Error fetching amenities:', error);
    }
  };

  const searchOwners = async (query) => {
    if (!query || query.length < 2) {
      setOwnerSearchResults([]);
      return;
    }
    try {
      const response = await api.get(`/api/users/search?q=${encodeURIComponent(query)}&is_owner=true`);
      setOwnerSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Error searching owners:', error);
    }
  };

  const searchHosts = async (query) => {
    if (!query || query.length < 2) {
      setHostSearchResults([]);
      return;
    }
    try {
      const response = await api.get(`/api/users/search?q=${encodeURIComponent(query)}&is_host=true`);
      setHostSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Error searching hosts:', error);
    }
  };

  useEffect(() => {
    if (!isOwner && ownerSearch) {
      const timer = setTimeout(() => searchOwners(ownerSearch), 300);
      return () => clearTimeout(timer);
    }
  }, [ownerSearch, isOwner]);

  useEffect(() => {
    if (!isHost && hostSearch) {
      const timer = setTimeout(() => searchHosts(hostSearch), 300);
      return () => clearTimeout(timer);
    }
  }, [hostSearch, isHost]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAmenityToggle = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenity_ids: prev.amenity_ids.includes(amenityId)
        ? prev.amenity_ids.filter(id => id !== amenityId)
        : [...prev.amenity_ids, amenityId]
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async (propertyId) => {
    if (selectedImages.length === 0) return;

    setUploadingImages(true);
    try {
      for (let i = 0; i < selectedImages.length; i++) {
        const file = selectedImages[i];
        const formData = new FormData();
        formData.append('file', file);

        // Upload the image file
        const uploadResponse = await api.post('/api/uploads/image?folder=properties', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const imageUrl = uploadResponse.data.image_url;

        // Add the image to the property
        await api.post(`/api/properties/${propertyId}/images`, {
          image_url: imageUrl,
          is_primary: i === 0 // First image is primary
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Erro ao enviar algumas imagens');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedVideos(prev => [...prev, ...files]);
  };

  const handleRemoveVideo = (index) => {
    setSelectedVideos(prev => prev.filter((_, i) => i !== index));
  };

  const uploadVideos = async (propertyId) => {
    if (selectedVideos.length === 0) return;

    setUploadingVideos(true);
    try {
      for (let i = 0; i < selectedVideos.length; i++) {
        const file = selectedVideos[i];
        const formData = new FormData();
        formData.append('file', file);

        // Upload the video file
        const uploadResponse = await api.post('/api/uploads/video?folder=properties', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const videoUrl = uploadResponse.data.video_url;

        // Add the video to the property
        await api.post(`/api/properties/${propertyId}/videos`, {
          video_url: videoUrl
        });
      }
    } catch (error) {
      console.error('Error uploading videos:', error);
      toast.error('Erro ao enviar alguns vídeos');
    } finally {
      setUploadingVideos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare property data with owner/host IDs
      const propertyData = {
        ...formData,
        owner_id: isOwner ? user.id : selectedOwner?.id,
        host_id: isHost ? user.id : selectedHost?.id
      };

      // Validate owner and host are selected
      if (!isOwner && !selectedOwner) {
        toast.error('Por favor, selecione o proprietário do imóvel');
        setLoading(false);
        return;
      }
      if (!isHost && !selectedHost) {
        toast.error('Por favor, selecione o anfitrião do imóvel');
        setLoading(false);
        return;
      }

      const response = await api.post('/api/properties', propertyData);
      const propertyId = response.data.property.id;
      const needsAuthorization = response.data.needs_authorization;

      // Upload images if any were selected
      if (selectedImages.length > 0) {
        await uploadImages(propertyId);
      }

      // Upload videos if any were selected
      if (selectedVideos.length > 0) {
        await uploadVideos(propertyId);
      }

      if (needsAuthorization) {
        toast.success('Propriedade criada! Aguardando autorização do ' + (isOwner ? 'anfitrião' : 'proprietário'));
        navigate('/dashboard');
      } else {
        toast.success('Propriedade criada com sucesso!');
        navigate(`/properties/${propertyId}`);
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Falha ao criar propriedade';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="property-form-page">
      <div className="container">
        <h1>{t('createProperty.title')}</h1>
        <p className="form-subtitle">{t('createProperty.subtitle')}</p>

        <form onSubmit={handleSubmit} className="property-form">
          {/* Basic Information */}
          <div className="form-section">
            <h2>{t('createProperty.basicInfo')}</h2>

            <div className="form-group">
              <label className="form-label">{t('createProperty.propertyTitle')} *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="form-input"
                required
                placeholder={t('createProperty.titlePlaceholder')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('createProperty.description')} *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-textarea"
                rows="6"
                required
                placeholder={t('createProperty.descriptionPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('createProperty.propertyType')} *</label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="apartment">{t('filters.apartment')}</option>
                <option value="house">{t('filters.house')}</option>
                <option value="villa">{t('filters.villa')}</option>
                <option value="condo">{t('filters.condo')}</option>
                <option value="cabin">{t('filters.cabin')}</option>
                <option value="cottage">{t('filters.cottage')}</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Categoria do Imóvel</label>
                <select
                  name="property_category"
                  value={formData.property_category}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="single_unit">Unidade Individual</option>
                  <option value="multi_unit">Multi-unidades (Prédio/Complexo)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Listagem</label>
                <select
                  name="listing_subtype"
                  value={formData.listing_subtype}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="entire_place">Lugar Inteiro</option>
                  <option value="private_room">Quarto Privado</option>
                  <option value="shared_room">Quarto Compartilhado</option>
                </select>
              </div>
            </div>
          </div>

          {/* Ownership & Management */}
          <div className="form-section">
            <h2>Propriedade e Gestão</h2>
            <div style={{
              background: '#e3f2fd',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start'
            }}>
              <FiInfo size={20} color="#1976d2" style={{ marginTop: '2px', flexShrink: 0 }} />
              <div style={{ fontSize: '0.9rem', color: '#1565c0' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600' }}>Sobre Proprietários e Anfitriões:</p>
                <p style={{ margin: 0 }}>
                  O <strong>proprietário</strong> é o dono do imóvel e terá acesso a relatórios de receita e ocupação.
                  O <strong>anfitrião</strong> é quem gerencia o imóvel no dia a dia (reservas, calendário, etc).
                  Podem ser a mesma pessoa ou pessoas diferentes.
                </p>
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={isOwner}
                  onChange={(e) => {
                    setIsOwner(e.target.checked);
                    if (e.target.checked) {
                      setSelectedOwner(null);
                      setOwnerSearch('');
                    }
                  }}
                />
                <span style={{ fontWeight: '600' }}>Sou o proprietário deste imóvel</span>
              </label>
            </div>

            {!isOwner && (
              <div className="form-group">
                <label className="form-label">Buscar Proprietário</label>
                <input
                  type="text"
                  value={ownerSearch}
                  onChange={(e) => setOwnerSearch(e.target.value)}
                  className="form-input"
                  placeholder={t('createProperty.ownerSearchPlaceholder')}
                />
                {ownerSearchResults.length > 0 && (
                  <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    marginTop: '0.5rem',
                    maxHeight: '200px',
                    overflow: 'auto',
                    background: 'white'
                  }}>
                    {ownerSearchResults.map(owner => (
                      <div
                        key={owner.id}
                        onClick={() => {
                          setSelectedOwner(owner);
                          setOwnerSearch('');
                          setOwnerSearchResults([]);
                        }}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          ':hover': { background: '#f8f9fa' }
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                      >
                        <div style={{ fontWeight: '600' }}>{owner.first_name} {owner.last_name}</div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>{owner.email}</div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedOwner && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.75rem',
                    background: '#e8f5e9',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600' }}>{selectedOwner.first_name} {selectedOwner.last_name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>{selectedOwner.email}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedOwner(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#d32f2f',
                        cursor: 'pointer',
                        fontSize: '1.2rem'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="form-group" style={{ marginTop: '1.5rem' }}>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={isHost}
                  onChange={(e) => {
                    setIsHost(e.target.checked);
                    if (e.target.checked) {
                      setSelectedHost(null);
                      setHostSearch('');
                    }
                  }}
                />
                <span style={{ fontWeight: '600' }}>Sou o anfitrião (vou gerenciar este imóvel)</span>
              </label>
            </div>

            {!isHost && (
              <div className="form-group">
                <label className="form-label">Buscar Anfitrião</label>
                <input
                  type="text"
                  value={hostSearch}
                  onChange={(e) => setHostSearch(e.target.value)}
                  className="form-input"
                  placeholder={t('createProperty.hostSearchPlaceholder')}
                />
                {hostSearchResults.length > 0 && (
                  <div style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    marginTop: '0.5rem',
                    maxHeight: '200px',
                    overflow: 'auto',
                    background: 'white'
                  }}>
                    {hostSearchResults.map(host => (
                      <div
                        key={host.id}
                        onClick={() => {
                          setSelectedHost(host);
                          setHostSearch('');
                          setHostSearchResults([]);
                        }}
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                      >
                        <div style={{ fontWeight: '600' }}>{host.first_name} {host.last_name}</div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>{host.email}</div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedHost && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.75rem',
                    background: '#e8f5e9',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600' }}>{selectedHost.first_name} {selectedHost.last_name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>{selectedHost.email}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedHost(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#d32f2f',
                        cursor: 'pointer',
                        fontSize: '1.2rem'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            )}

            {(!isOwner || !isHost) && (selectedOwner || selectedHost) && (
              <div style={{
                background: '#fff8e1',
                padding: '1rem',
                borderRadius: '8px',
                marginTop: '1.5rem',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start'
              }}>
                <FiInfo size={20} color="#f57c00" style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: '0.9rem', color: '#e65100' }}>
                  <p style={{ margin: 0, fontWeight: '600' }}>
                    ⚠️ Este imóvel precisará de autorização
                  </p>
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    O {!isOwner ? 'proprietário' : 'anfitrião'} precisará aprovar antes do imóvel ficar ativo na plataforma.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Location */}
          <div className="form-section">
            <h2>{t('createProperty.location')}</h2>

            <div className="form-group">
              <label className="form-label">{t('createProperty.address')} *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.city')} *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('createProperty.state')}</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.country')} *</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('createProperty.zipCode')}</label>
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="form-section">
            <h2>{t('createProperty.details')}</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.bedrooms')} *</label>
                <input
                  type="number"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('createProperty.bathrooms')} *</label>
                <input
                  type="number"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.beds')} *</label>
                <input
                  type="number"
                  name="beds"
                  value={formData.beds}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Área (m²)</label>
                <input
                  type="number"
                  name="square_footage"
                  value={formData.square_footage}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                  placeholder={t('common.squareFootagePlaceholder')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.maxGuests')} *</label>
                <input
                  type="number"
                  name="max_guests"
                  value={formData.max_guests}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Hóspedes Inclusos no Preço Base *</label>
                <input
                  type="number"
                  name="base_guests"
                  value={formData.base_guests}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  max={formData.max_guests}
                  required
                />
                <small className="form-hint">Número de hóspedes já inclusos no preço da diária</small>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="form-section">
            <h2>{t('createProperty.pricing')}</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.pricePerNight')} (R$) *</label>
                <input
                  type="number"
                  name="price_per_night"
                  value={formData.price_per_night}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                  required
                />
                <small className="form-hint">Preço base para até {formData.base_guests} hóspede(s)</small>
              </div>

              <div className="form-group">
                <label className="form-label">Preço por Hóspede Adicional (R$)</label>
                <input
                  type="number"
                  name="extra_guest_price"
                  value={formData.extra_guest_price}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
                <small className="form-hint">Valor cobrado por cada hóspede além dos {formData.base_guests} inclusos</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.cleaningFee')} (R$)</label>
                <input
                  type="number"
                  name="cleaning_fee"
                  value={formData.cleaning_fee}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Taxa de Serviço (R$)</label>
                <input
                  type="number"
                  name="service_fee"
                  value={formData.service_fee}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Noites Mínimas</label>
                <input
                  type="number"
                  name="minimum_nights"
                  value={formData.minimum_nights}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Noites Máximas</label>
                <input
                  type="number"
                  name="maximum_nights"
                  value={formData.maximum_nights}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h2>Fotos do Imóvel</h2>
            <div className="form-group">
              <label className="form-label">Adicionar Fotos</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="form-input"
              />
              <small className="form-hint">Você pode selecionar múltiplas imagens. A primeira será a foto principal.</small>
            </div>

            {selectedImages.length > 0 && (
              <div className="selected-images">
                <p><strong>{selectedImages.length}</strong> imagem(ns) selecionada(s) - Arraste para reordenar:</p>
                <DraggableMediaList
                  items={selectedImages.map((file, idx) => ({
                    id: `new-${idx}`,
                    preview: URL.createObjectURL(file),
                    file: file
                  }))}
                  onReorder={(reordered) => setSelectedImages(reordered.map(item => item.file))}
                  onRemove={handleRemoveImage}
                  type="image"
                />
              </div>
            )}
          </div>

          {/* Videos */}
          <div className="form-section">
            <h2>Vídeos do Imóvel</h2>
            <div className="form-group">
              <label className="form-label">Adicionar Vídeos</label>
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleVideoSelect}
                className="form-input"
              />
              <small className="form-hint">Você pode selecionar múltiplos vídeos. Formatos: MP4, MOV, AVI, WEBM, MKV.</small>
            </div>

            {selectedVideos.length > 0 && (
              <div className="selected-videos">
                <p><strong>{selectedVideos.length}</strong> vídeo(s) selecionado(s) - Arraste para reordenar:</p>
                <DraggableMediaList
                  items={selectedVideos.map((file, idx) => ({
                    id: `new-${idx}`,
                    preview: URL.createObjectURL(file),
                    file: file
                  }))}
                  onReorder={(reordered) => setSelectedVideos(reordered.map(item => item.file))}
                  onRemove={handleRemoveVideo}
                  type="video"
                />
              </div>
            )}
          </div>

          {/* Amenities */}
          <div className="form-section">
            <h2>{t('createProperty.amenities')}</h2>
            <div className="amenities-grid">
              {amenities.map(amenity => (
                <label key={amenity.id} className="amenity-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.amenity_ids.includes(amenity.id)}
                    onChange={() => handleAmenityToggle(amenity.id)}
                  />
                  <span>{amenity.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* House Rules */}
          <div className="form-section">
            <h2>{t('propertyDetail.houseRules')}</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Horário de Check-in</label>
                <input
                  type="time"
                  name="check_in_time"
                  value={formData.check_in_time}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Horário de Check-out</label>
                <input
                  type="time"
                  name="check_out_time"
                  value={formData.check_out_time}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Política de Cancelamento</label>
              <select
                name="cancellation_policy"
                value={formData.cancellation_policy}
                onChange={handleChange}
                className="form-select"
              >
                <option value="flexible">Flexível</option>
                <option value="moderate">Moderada</option>
                <option value="strict">Rigorosa</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{t('propertyDetail.houseRules')}</label>
              <textarea
                name="house_rules"
                value={formData.house_rules}
                onChange={handleChange}
                className="form-textarea"
                rows="4"
                placeholder={t('createProperty.houseRulesPlaceholder')}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/host/dashboard')}
              className="btn btn-secondary btn-lg"
              disabled={loading || uploadingImages || uploadingVideos}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading || uploadingImages || uploadingVideos}
            >
              {uploadingVideos ? 'Enviando vídeos...' : uploadingImages ? 'Enviando fotos...' : loading ? 'Criando...' : t('createProperty.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProperty;
